/** Building invoice tab: GET /property/invoice, then client filter, sort, and pagination. */

import { clampPageNumber } from './listPagination';
import { isInvoicePaid } from './invoicePaymentStatus';
import { normalizeDateOnly } from './dateOnly';
import { resolveInvoiceDateRangeForApi } from './invoiceDateRangeFilter';

export function normalizeBuildingInvoiceResponse(response) {
  const invoiceData = Array.isArray(response?.invoiceData) ? response.invoiceData : [];
  return {
    invoiceData,
    schedule_date: response?.schedule_date ?? null,
    total: invoiceData.length,
  };
}

export function getInvoiceUpdateStatus(invoice, scheduleDate) {
  if (!scheduleDate) return '';
  const cutoff = new Date(new Date(scheduleDate) - 86400000);
  if (new Date(invoice.createdAt) > cutoff) return 'New';
  if (new Date(invoice.lastUpdatedTime) > cutoff) return 'Update';
  return '';
}

export function getInvoiceDisputeStatus(invoice) {
  if (!invoice?.dispute?.length) return '';
  const lastDispute = invoice.dispute[invoice.dispute.length - 1];
  if (lastDispute?.total_amount != null && lastDispute.total_amount !== invoice.total_amount) {
    return 'updated';
  }
  return 'dispute';
}

function matchesSearch(invoice, searchText) {
  const query = `${searchText || ''}`.trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    invoice.doc_number,
    invoice.sf_building_name,
    invoice.sf_landlord_name,
    invoice.invoice_id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function matchesPayment(invoice, selectedPayment) {
  if (!Array.isArray(selectedPayment) || selectedPayment.length === 0) return true;
  const paid = isInvoicePaid(invoice.balance);
  const wantsPaid = selectedPayment.includes('1');
  const wantsUnpaid = selectedPayment.includes('-1');
  if (wantsPaid && wantsUnpaid) return true;
  if (wantsPaid) return paid;
  if (wantsUnpaid) return !paid;
  return true;
}

function matchesDateRange(invoice, columnValue, range) {
  const { startDate, endDate } = resolveInvoiceDateRangeForApi(range);
  if (!startDate && !endDate) return true;

  const value = normalizeDateOnly(columnValue);
  if (!value) return false;

  if (startDate && value < startDate) return false;
  if (endDate && value > endDate) return false;
  return true;
}

/**
 * @param {object[]} invoiceData
 * @param {string|null} scheduleDate
 * @param {object} criteria
 */
export function filterBuildingInvoices(invoiceData, scheduleDate, criteria) {
  const rows = Array.isArray(invoiceData) ? invoiceData : [];
  const {
    searchText = '',
    selectedPayment = [],
    selectedInvoiceStatus = [],
    selectedDueDateRange = null,
    selectedInvoiceDateRange = null,
    selectedUpdateStatus = [],
    selectedDisputed = [],
  } = criteria || {};

  return rows.filter((invoice) => {
    if (!matchesSearch(invoice, searchText)) return false;
    if (!matchesPayment(invoice, selectedPayment)) return false;
    if (
      selectedInvoiceStatus.length > 0 &&
      !selectedInvoiceStatus.includes(invoice.invoice_status)
    ) {
      return false;
    }
    if (!matchesDateRange(invoice, invoice.due_date, selectedDueDateRange)) return false;
    if (!matchesDateRange(invoice, invoice.invoice_date, selectedInvoiceDateRange)) return false;

    const updateStatus = getInvoiceUpdateStatus(invoice, scheduleDate);
    if (selectedUpdateStatus.length > 0) {
      const wantsNa = selectedUpdateStatus.includes('');
      const wantsNew = selectedUpdateStatus.includes('New');
      const wantsUpdate = selectedUpdateStatus.includes('Update');
      const matchNa = wantsNa && !updateStatus;
      const matchNew = wantsNew && updateStatus === 'New';
      const matchUpdate = wantsUpdate && updateStatus === 'Update';
      if (!(matchNa || matchNew || matchUpdate)) return false;
    }

    const disputeStatus = getInvoiceDisputeStatus(invoice);
    if (selectedDisputed.length > 0) {
      const wantsNa = selectedDisputed.includes('');
      const wantsDispute = selectedDisputed.includes('dispute');
      const wantsUpdated = selectedDisputed.includes('updated');
      const matchNa = wantsNa && !disputeStatus;
      const matchDispute = wantsDispute && disputeStatus === 'dispute';
      const matchUpdated = wantsUpdated && disputeStatus === 'updated';
      if (!(matchNa || matchDispute || matchUpdated)) return false;
    }

    return true;
  });
}

const SORT_FIELD_RESOLVERS = {
  invoice_id: (invoice) => invoice.doc_number ?? '',
  payment: (invoice) => isInvoicePaid(invoice.balance),
  invoice_status: (invoice) => invoice.invoice_status ?? '',
  sf_landlord_name: (invoice) => invoice.sf_landlord_name ?? '',
  sf_building_name: (invoice) => invoice.sf_building_name ?? '',
  total_amount: (invoice) => Number(invoice.total_amount) || 0,
  balance: (invoice) => Number(invoice.balance) || 0,
  due_date: (invoice) => invoice.due_date ?? '',
  invoice_date: (invoice) => invoice.invoice_date ?? '',
  lastUpdatedTime: (invoice) => invoice.lastUpdatedTime ?? '',
};

export function sortBuildingInvoices(invoiceData, sortOptions, scheduleDate) {
  const rows = Array.isArray(invoiceData) ? [...invoiceData] : [];
  if (!sortOptions || typeof sortOptions !== 'object') return rows;

  const active = Object.entries(sortOptions).find(
    ([, direction]) => direction === 1 || direction === -1,
  );
  if (!active) return rows;

  const [field, direction] = active;
  const resolve =
    field === 'update_status'
      ? (invoice) => getInvoiceUpdateStatus(invoice, scheduleDate)
      : field === 'dispute_status'
        ? (invoice) => getInvoiceDisputeStatus(invoice)
        : SORT_FIELD_RESOLVERS[field];

  if (!resolve) return rows;

  const multiplier = direction === -1 ? -1 : 1;
  return rows.sort((a, b) => {
    const left = resolve(a);
    const right = resolve(b);
    if (left === right) return 0;
    if (left == null || left === '') return 1 * multiplier;
    if (right == null || right === '') return -1 * multiplier;
    if (typeof left === 'boolean' || typeof right === 'boolean') {
      return (Number(left) - Number(right)) * multiplier;
    }
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * multiplier;
    }
    return String(left).localeCompare(String(right), undefined, { numeric: true }) * multiplier;
  });
}

export function paginateInvoiceRows(invoiceData, pageNumber, rowsPerPage) {
  const rows = Array.isArray(invoiceData) ? invoiceData : [];
  const safePage = clampPageNumber(pageNumber, rows.length, rowsPerPage);
  const start = safePage * rowsPerPage;
  return rows.slice(start, start + rowsPerPage);
}
